<template>
  <div class="lead-deals">
    <LoadingSpinner v-if="loading" />

    <EmptyState
      v-else-if="deals.length === 0"
      :title="$t('deals.empty_state_lead.title')"
      :description="$t('deals.empty_state_lead.description')"
      icon="handshake"
    >
      <Btn
        :text="$t('deals.create_deal')"
        icon="plus"
        variant="secondary"
        @click="createDeal"
      />
    </EmptyState>

    <div v-else class="lead-deals__content">
      <!-- Deals List with Activities -->
      <div
        v-for="deal in deals"
        :key="deal.id"
        class="lead-deals__deal"
      >
        <!-- Deal Header -->
        <div class="lead-deals__deal-header" @click="openDeal(deal)">
          <div class="lead-deals__deal-info">
            <div class="lead-deals__deal-title">
              <span class="lead-deals__deal-name">{{ deal.title }}</span>
              <span
                class="lead-deals__deal-stage"
                :style="{ backgroundColor: deal.stage?.color + '20', color: deal.stage?.color }"
              >
                {{ deal.stage?.name }}
              </span>
            </div>
            <div class="lead-deals__deal-meta">
              <span class="lead-deals__deal-value" v-if="deal.value">
                {{ formatCurrency(deal.value) }}
              </span>
              <span class="lead-deals__deal-date">
                {{ formatDate(deal.created_at) }}
              </span>
            </div>
          </div>
          <BaseIcon name="chevron-right" size="16" class="lead-deals__deal-arrow" />
        </div>

        <!-- Deal Activities Timeline -->
        <div class="activity-list" v-if="deal.activities && deal.activities.length > 0">
          <div
            v-for="activity in deal.activities.slice(0, 3)"
            :key="activity.id"
            class="activity-item"
            :class="`activity-item--${activity.type}`"
          >
            <div class="activity-marker">
              <div class="activity-dot"></div>
              <div class="activity-line"></div>
            </div>
            <div class="activity-content">
              <div class="activity-header">
                <div class="activity-icon" :class="`activity-icon--${activity.type}`">
                  <BaseIcon :name="getActivityIcon(activity.type)" size="12" />
                </div>
                <span class="activity-subject">{{ activity.subject }}</span>
                <span class="activity-timestamp">{{ formatTimeAgo(activity.created_at) }}</span>
              </div>
              <p class="activity-description" v-if="activity.description">
                {{ activity.description }}
              </p>
            </div>
          </div>

          <!-- Show more link if more than 3 activities -->
          <div
            class="lead-deals__show-more"
            v-if="deal.activities.length > 3"
            @click="openDeal(deal)"
          >
            {{ $t('deals.show_more_activities', { count: deal.activities.length - 3 }) }}
          </div>
        </div>

        <!-- No activities message -->
        <div class="lead-deals__no-activities" v-else>
          <BaseIcon name="clock" size="14" />
          <span>{{ $t('deals.no_activities') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, onMounted, ref, getCurrentInstance } from 'vue';
import { dealService } from '@/services/dealService';
import { useUIStore } from '@/store/uiStore';
import { formatDate, formatTimeAgo } from '@/utils/date';

import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
import EmptyState from '@/components/partials/EmptyState.vue';
import Btn from '@/components/partials/Btn.vue';
import BaseIcon from '@shared/components/BaseIcon.vue';

export default defineComponent({
  name: 'LeadDeals',

  components: {
    LoadingSpinner,
    EmptyState,
    Btn,
    BaseIcon,
  },

  props: {
    leadId: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const instance = getCurrentInstance();
    const $t = instance.appContext.config.globalProperties.$t;
    const uiStore = useUIStore();

    const loading = ref(true);
    const deals = ref([]);

    const formatCurrency = (value) => {
      return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value || 0);
    };

    const getActivityIcon = (type) => {
      const icons = {
        call: 'phone',
        email: 'mail',
        email_sent: 'send',
        whatsapp: 'message-circle',
        note: 'file-text',
        meeting: 'calendar',
        stage_change: 'arrow-right',
        deal_created: 'plus-circle',
        deal_won: 'trophy',
        deal_lost: 'x-circle',
      };
      return icons[type] || 'activity';
    };

    const createDeal = async () => {
      const { default: CreateDealModal } = await import('@/components/deals/CreateDealModal.vue');
      const modalId = uiStore.addModal(CreateDealModal, {
        title: $t('deals.create_deal'),
        preselectedLeadId: props.leadId,
        onSubmit: async (deal) => {
          uiStore.closeModal(modalId);
          // Refresh deals list
          await fetchDeals();
        },
      });
    };

    const openDeal = async (deal) => {
      const { default: DealDetailDrawer } = await import('@/components/deals/DealDetailDrawer.vue');
      uiStore.addModal(DealDetailDrawer, {
        title: deal.title,
        dealId: deal.id,
        size: 'drawer',
        onClose: () => {
          // Refresh deals on close in case something changed
          fetchDeals();
        },
      });
    };

    const fetchDeals = async () => {
      try {
        const response = await dealService.getDealsByLead(props.leadId);
        deals.value = response.deals || [];
      } catch (error) {
        console.error('Failed to fetch deals:', error);
        deals.value = [];
      } finally {
        loading.value = false;
      }
    };

    onMounted(fetchDeals);

    return {
      loading,
      deals,
      formatCurrency,
      formatDate,
      formatTimeAgo,
      getActivityIcon,
      createDeal,
      openDeal,
    };
  },
});
</script>

<style scoped lang="scss">
.lead-deals {
  &__content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  &__deal {
    @include card;
  }

  &__deal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
    border-bottom: 1px solid var(--slate-200);
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 0.8;
    }
  }

  &__deal-info {
    flex: 1;
    min-width: 0;
  }

  &__deal-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-bottom: 0.25rem;
  }

  &__deal-name {
    font-size: 1rem;
    font-weight: 600;
    color: var(--slate-900);
  }

  &__deal-stage {
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
  }

  &__deal-meta {
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: 0.875rem;
    color: var(--slate-500);
  }

  &__deal-value {
    font-weight: 600;
    color: var(--slate-700);
  }

  &__deal-arrow {
    color: var(--slate-400);
    flex-shrink: 0;
  }

  &__no-activities {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background: var(--slate-50);
    border-radius: 0.5rem;
    font-size: 0.8125rem;
    color: var(--slate-400);

    .icon {
      color: var(--slate-300);
    }
  }

  &__show-more {
    padding: 0.5rem 0 0 2rem;
    font-size: 0.8125rem;
    color: var(--brand-600);
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
      color: var(--brand-700);
      text-decoration: underline;
    }
  }
}

// Timeline styles (matching DealActivities.vue)
.activity-list {
  position: relative;
  padding-left: 1.5rem;

  .activity-item {
    position: relative;
    padding: 0.5rem 0;

    // Color mapping per type
    &--call {
      --activity-color: var(--blue-500);
    }
    &--email, &--email_sent {
      --activity-color: var(--purple-500);
    }
    &--whatsapp {
      --activity-color: var(--green-500);
    }
    &--note {
      --activity-color: var(--amber-500);
    }
    &--meeting {
      --activity-color: var(--indigo-500);
    }
    &--stage_change {
      --activity-color: var(--teal-500);
    }
    &--deal_created {
      --activity-color: var(--brand-500);
    }
    &--deal_won {
      --activity-color: var(--green-500);
    }
    &--deal_lost {
      --activity-color: var(--red-500);
    }

    .activity-marker {
      position: absolute;
      left: -1.5rem;
      top: 0;
      bottom: 0;
      width: 2px;
      display: flex;
      flex-direction: column;
      align-items: center;

      .activity-dot {
        width: 10px;
        height: 10px;
        background: var(--activity-color, var(--slate-400));
        border-radius: 50%;
        margin: 0.5rem 0 0.25rem 0;
        flex-shrink: 0;
      }

      .activity-line {
        flex: 1;
        width: 2px;
        background: var(--slate-200);
      }
    }

    &:last-child .activity-marker .activity-line {
      display: none;
    }

    .activity-content {
      background: var(--slate-50);
      border-radius: 0.375rem;
      padding: 0.75rem;
    }

    .activity-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .activity-icon {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      &--call {
        background: var(--blue-100);
        color: var(--blue-600);
      }
      &--email, &--email_sent {
        background: var(--purple-100);
        color: var(--purple-600);
      }
      &--whatsapp {
        background: var(--green-100);
        color: var(--green-600);
      }
      &--note {
        background: var(--amber-100);
        color: var(--amber-600);
      }
      &--meeting {
        background: var(--indigo-100);
        color: var(--indigo-600);
      }
      &--stage_change {
        background: var(--teal-100);
        color: var(--teal-600);
      }
      &--deal_created {
        background: var(--brand-100);
        color: var(--brand-600);
      }
      &--deal_won {
        background: var(--green-100);
        color: var(--green-600);
      }
      &--deal_lost {
        background: var(--red-100);
        color: var(--red-600);
      }
    }

    .activity-subject {
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--slate-900);
      flex: 1;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .activity-timestamp {
      font-size: 0.6875rem;
      color: var(--slate-400);
      flex-shrink: 0;
    }

    .activity-description {
      font-size: 0.75rem;
      color: var(--slate-500);
      margin: 0.25rem 0 0 0;
      line-height: 1.4;
    }
  }
}
</style>
